// @source 40 CFR 261.31 (F codes) / 261.32 (K codes) / 261.33 (P/U codes) /
// 261.24 (D toxicity characteristic) via eCFR; Commission Decision 2000/532/EC
// (EWC) via EUR-Lex. The crosswalk is editorial: each pair must be defensible
// from both regulation texts, and every note states the scope difference —
// RCRA process/product-based listing vs. EWC's broader waste-stream code.

export interface CrosswalkPair {
  rcra: string;          // "F001"
  ewc: string;            // "14 06 02" — catalogue spacing, no star
  relation: 'equivalent' | 'overlaps';
  note: string;           // one sentence on scope difference — required
}

function normalizeEwc(code: string): string {
  return code.replace(/\*/g, '').trim().replace(/\s+/g, ' ');
}

export const RCRA_EWC_CROSSWALK: CrosswalkPair[] = [
  {
    rcra: 'F001',
    ewc: '14 06 02',
    relation: 'overlaps',
    note: 'EWC 14 06 02 covers all halogenated solvent waste from any use; F001 is limited to the specific halogenated solvents used in degreasing operations.',
  },
  {
    rcra: 'F002',
    ewc: '14 06 02',
    relation: 'overlaps',
    note: 'F002 lists other halogenated solvents used outside degreasing; EWC 14 06 02 has no use restriction, only requiring the waste to be a halogenated solvent.',
  },
  {
    rcra: 'F003',
    ewc: '14 06 03',
    relation: 'overlaps',
    note: 'F003 names nine specific non-halogenated solvents and their mixtures; EWC 14 06 03 covers any other non-halogenated solvent waste with no named-chemical restriction.',
  },
  {
    rcra: 'F005',
    ewc: '14 06 03',
    relation: 'overlaps',
    note: 'F005 names eight specific non-halogenated solvents from any generating industry; EWC 14 06 03 is the broader non-halogenated solvent waste code with no chemical list.',
  },
  {
    rcra: 'F005',
    ewc: '07 01 04',
    relation: 'overlaps',
    note: 'F005 is a fixed list of solvents from any industry; EWC 07 01 04 scopes to spent organic solvents and washing liquids arising specifically from organic chemical process operations.',
  },
  {
    rcra: 'F006',
    ewc: '11 01 09',
    relation: 'overlaps',
    note: 'F006 is electroplating wastewater sludge only, excluding several named sub-processes; EWC 11 01 09 covers hazardous sludges and filter cakes from any metal surface-treatment process.',
  },
  {
    rcra: 'F008',
    ewc: '11 01 09',
    relation: 'overlaps',
    note: 'F008 is cyanide plating-bath residue specifically; EWC 11 01 09 is the general hazardous metal-finishing sludge and filter-cake code, cyanide-bearing or not.',
  },
  {
    rcra: 'K048',
    ewc: '05 01 06',
    relation: 'overlaps',
    note: 'K048 is dissolved air flotation float from petroleum refining specifically; EWC 05 01 06 is the broader oily-sludge code covering any refinery plant-maintenance residue.',
  },
  {
    rcra: 'K049',
    ewc: '05 01 06',
    relation: 'overlaps',
    note: 'K049 is slop-oil emulsion solids from refining specifically; EWC 05 01 06 covers oily maintenance sludges from petroleum refining generally, not one named residue stream.',
  },
  {
    rcra: 'K050',
    ewc: '05 01 06',
    relation: 'overlaps',
    note: 'K050 is heat-exchanger bundle cleaning sludge specifically; EWC 05 01 06 is the general oily-sludge-from-plant-maintenance code that also covers K050-type residues.',
  },
  {
    rcra: 'K051',
    ewc: '05 01 06',
    relation: 'overlaps',
    note: 'K051 is API separator sludge from refining specifically; EWC 05 01 06 covers oily sludges from refinery plant maintenance broadly, not the separator process alone.',
  },
  {
    rcra: 'K052',
    ewc: '05 01 03',
    relation: 'overlaps',
    note: 'K052 is leaded tank-bottom residue from petroleum refining only; EWC 05 01 03 covers all refinery tank-bottom sludges, leaded or unleaded.',
  },
  {
    rcra: 'D008',
    ewc: '16 06 01',
    relation: 'overlaps',
    note: 'D008 applies to any waste stream that fails the lead TCLP threshold, regardless of source; EWC 16 06 01 is narrowly scoped to discarded lead-acid batteries.',
  },
  {
    rcra: 'D009',
    ewc: '20 01 21',
    relation: 'overlaps',
    note: 'D009 applies to any waste with mercury above the TCLP threshold from any source; EWC 20 01 21 is narrowly scoped to fluorescent tubes and other mercury-containing lamps.',
  },
  {
    rcra: 'D001',
    ewc: '13 07 03',
    relation: 'overlaps',
    note: 'D001 characterizes by flash point below 60C regardless of source; EWC 13 07 03 is scoped specifically to waste fuels and fuel mixtures, one possible D001 stream among many.',
  },
  {
    rcra: 'D001',
    ewc: '14 06 03',
    relation: 'overlaps',
    note: 'D001 is a pure flash-point characteristic applying to any waste; EWC 14 06 03 is scoped to waste organic solvents, which are commonly but not always ignitable.',
  },
  {
    rcra: 'U002',
    ewc: '14 06 03',
    relation: 'overlaps',
    note: 'U002 covers only unused, discarded acetone product; EWC 14 06 03 covers spent non-halogenated solvent waste from any process, used or not, not one named chemical.',
  },
  {
    rcra: 'U002',
    ewc: '07 01 04',
    relation: 'overlaps',
    note: 'U002 is the unused commercial acetone chemical being discarded; EWC 07 01 04 covers spent organic solvents from organic chemical process operations, not unused product.',
  },
  {
    rcra: 'P050',
    ewc: '02 01 08',
    relation: 'overlaps',
    note: 'P050 is unused Endosulfan product being discarded specifically; EWC 02 01 08 is the broader agrochemical waste code covering any hazardous pesticide residue or formulation.',
  },
  {
    rcra: 'P089',
    ewc: '02 01 08',
    relation: 'overlaps',
    note: 'P089 is unused Parathion product being discarded specifically; EWC 02 01 08 covers hazardous agrochemical waste generally, including formulation residues beyond one named pesticide.',
  },
  {
    rcra: 'K061',
    ewc: '10 02 07',
    relation: 'overlaps',
    note: 'K061 is emission control dust from electric-arc-furnace steelmaking specifically; EWC 10 02 07 is the general hazardous solid gas-treatment waste code covering any thermal metal process.',
  },
  {
    rcra: 'K062',
    ewc: '11 01 05',
    relation: 'overlaps',
    note: 'K062 is spent pickle liquor limited to SIC 331/332 steel finishing facilities; EWC 11 01 05 covers pickling-acid waste from any metal surface-treatment industry.',
  },
  {
    rcra: 'F032',
    ewc: '03 02 02',
    relation: 'overlaps',
    note: 'F032 covers wastes from wood-preserving processes that use chlorophenolic (e.g. pentachlorophenol) formulations; EWC 03 02 02 is the matching wood-preservation waste code for organochlorinated preservatives — the difference is RCRA\'s process/formulation basis vs EWC\'s preservative-chemistry basis.',
  },
  {
    rcra: 'F034',
    ewc: '03 02 01',
    relation: 'overlaps',
    note: 'F034 covers wastes from wood-preserving processes that use creosote; EWC 03 02 01 is the wood-preservation waste code for non-halogenated organic preservatives, which includes creosote — the scope difference is RCRA\'s process basis vs EWC\'s preservative-chemistry basis.',
  },
  {
    rcra: 'F035',
    ewc: '03 02 04',
    relation: 'overlaps',
    note: 'F035 covers wastes from wood-preserving processes that use inorganic arsenic or chromium preservatives (e.g. CCA); EWC 03 02 04 is the matching wood-preservation waste code for inorganic preservatives — the difference is RCRA\'s process basis vs EWC\'s preservative-chemistry basis.',
  },
  {
    rcra: 'U080',
    ewc: '14 06 02',
    relation: 'overlaps',
    note: 'U080 is the unused commercial methylene chloride chemical product being discarded; EWC 14 06 02 covers halogenated solvent waste streams from any process, not the unused product itself.',
  },
  {
    rcra: 'F002',
    ewc: '14 06 04',
    relation: 'overlaps',
    note: 'F002 includes still bottoms and solid residues from halogenated-solvent recovery; EWC 14 06 04 is the sludge and solid-waste code specific to halogenated-solvent-bearing residues.',
  },
];

const byRcra = new Map<string, CrosswalkPair[]>();
const byEwc = new Map<string, CrosswalkPair[]>();
for (const pair of RCRA_EWC_CROSSWALK) {
  const rcraKey = pair.rcra.toUpperCase().trim();
  if (!byRcra.has(rcraKey)) byRcra.set(rcraKey, []);
  byRcra.get(rcraKey)!.push(pair);

  const ewcKey = normalizeEwc(pair.ewc);
  if (!byEwc.has(ewcKey)) byEwc.set(ewcKey, []);
  byEwc.get(ewcKey)!.push(pair);
}

/** EWC crosswalk pairs for a given RCRA code (e.g. "F001"). */
export function getEwcEquivalents(rcraCode: string): CrosswalkPair[] {
  return byRcra.get(rcraCode.toUpperCase().trim()) ?? [];
}

/** RCRA crosswalk pairs for a given EWC code (e.g. "14 06 02" or "14 06 02*"). */
export function getRcraEquivalents(ewcCode: string): CrosswalkPair[] {
  return byEwc.get(normalizeEwc(ewcCode)) ?? [];
}
